<template>
    <div class="product-map">
        <div v-if="mapActive" id="airportProductsMap" ref="mapContainer" class="map-container"></div>
        <div v-if="!mapActive && blok.openMapBtn && blok.mapPlaceholderImage?.filename" class="map-placeholder">
            <NuxtImg
                v-if="blok.mapPlaceholderImage?.filename"
                :src="blok.mapPlaceholderImage.filename"
                :title="blok.mapPlaceholderImage.title"
                :alt="blok.mapPlaceholderImage.alt"
                loading="lazy"
                provider="storyblok"
                format="webp"
                densities="x1"
            />
        </div>
        <div v-if="blok.openMapBtn" class="map-btn" :class="{ 'map-active': mapActive }">
            <button v-if="!mapActive" @click="openMap()">
                <NuxtIcon name="ri:map-2-line" />
                View on Map
            </button>
            <button v-else @click="closeMap()">
                <NuxtIcon name="ri:close-line" class="close-icon" />
                <span>Close map</span>
            </button>
        </div>
        <div v-if="mapActive" ref="hiddenContainer" style="display: none">
            <ProductCard v-if="activeProduct" :product-data="activeProduct" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { Loader } from '@googlemaps/js-api-loader';
import type { IAirportEntity, IAirportProducts, IHotelProduct, IParkingProduct } from '@nuxt-monorepo/ui/types';
import type { IApiProductMap, IMarkerData } from '@nuxt-monorepo/ui/types/components/Map/ApiProductMap';
import ProductCard from '@nuxt-monorepo/ui/components/Map/ProductCard.vue';
import { filterProducts } from '@nuxt-monorepo/ui/utils/apiFilter';

const { blok, products, airport } = defineProps<{
    blok: IApiProductMap;
    products: IAirportProducts;
    airport: IAirportEntity;
}>();

const config: any = useRuntimeConfig();
let googleInitLoad: Loader | null = null;
let coreLib: google.maps.CoreLibrary | null = null;
let mapsLib: google.maps.MapsLibrary | null = null;
let markerLib: google.maps.MarkerLibrary | null = null;
const mapContainer = ref<HTMLDivElement>();
let mapInstance: google.maps.Map | null = null;
const mapActive = ref(!blok.openMapBtn);

const hiddenContainer = useTemplateRef('hiddenContainer');

const mapMarkers = ref<google.maps.marker.AdvancedMarkerElement[]>([]);

const openMap = () => {
    mapActive.value = true;
    initGoogleMaps();
};

const closeMap = () => {
    mapActive.value = false;
};

function initGoogleMaps() {
    if (import.meta.client) {
        googleInitLoad = new Loader({
            apiKey: config?.public.GOOGLE_API_KEY,
            version: config?.public.GOOGLE_API_VERSION,
            language: 'en',
            libraries: ['core', 'maps', 'marker'],
        });

        // Default Map Location
        const mapOptions = {
            mapId: config?.public.GOOGLE_MAP_ID,
            center: {
                lat: Number(airport?.latitude ?? 51.5074),
                lng: Number(airport?.longitude ?? 0.1278),
            },
            zoom: Number(blok.Zoom) ?? 14,
        };

        googleInitLoad.load().then(async () => {
            // load libs
            coreLib = (await google.maps.importLibrary('core')) as google.maps.CoreLibrary;
            mapsLib = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;
            markerLib = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

            // create map and place markers for airport & products
            mapInstance = new mapsLib.Map(mapContainer.value as HTMLElement, mapOptions);

            placeMarkers();
            centerMap();
        });
    }
}

const isIHotelProduct = (product: IHotelProduct | IParkingProduct): product is IHotelProduct => {
    return (product as IParkingProduct).geo?.latitude === undefined;
};
const productList = filterProducts(blok, products);

// collect marker data for Airport & Product
const markersData = computed(() => {
    const data: IMarkerData[] = [];

    [...(productList ?? [])].forEach((product) => {
        data.push({
            type: 'AirportHotels',
            position: {
                lat: Number(
                    isIHotelProduct(product) ? (product.latitude ?? product.address.latitude) : product.geo?.latitude
                ),
                lng: Number(
                    isIHotelProduct(product) ? (product.longitude ?? product.address.longitude) : product.geo?.longitude
                ),
            },
            title: product.name,
            icon: blok.productMarkerIcon?.filename || null,
            productData: product,
        });
    });

    data.push({
        type: 'airport',
        position: {
            lat: Number(airport?.latitude ?? 51.5074),
            lng: Number(airport?.longitude ?? 0.1278),
        },
        icon: blok.icon?.filename || null,
    });
    return data;
});
const activeProduct = ref(markersData.value?.[0]?.productData);

function placeMarkers() {
    if (mapsLib && mapInstance && markerLib) {
        mapInstance.setClickableIcons(false);
        const infoWindow = new google.maps.InfoWindow();
        infoWindow.setOptions({ maxWidth: 350 });
        // place product markers
        markersData.value.forEach((markerData) => {
            const markerOptions: google.maps.marker.AdvancedMarkerElementOptions = {
                map: mapInstance,
                position: markerData.position,
                title: markerData.title,
            };

            if (markerData.icon) {
                const markerIcon = document.createElement('img');
                markerIcon.src = markerData.icon;
                markerIcon.style.maxHeight = '34px';
                if (markerData.type !== 'airport') {
                    markerIcon.classList.add('product-marker-icon');
                }
                markerOptions.content = markerIcon;
            }

            const marker = new (markerLib as google.maps.MarkerLibrary).AdvancedMarkerElement(markerOptions);
            mapMarkers.value.push(marker);

            // exclude airport marker from showing product card on click
            if (markerData.type === 'airport') return;
            marker.addListener('click', async (e: google.maps.MapMouseEvent) => {
                e.stop();
                activeProduct.value = markerData.productData;
                await nextTick();
                infoWindow.setContent(hiddenContainer.value?.innerHTML);
                infoWindow.open(mapInstance, marker);
            });
            mapInstance?.addListener('click', () => {
                infoWindow.close();
            });
        });
    }
}

// center map to fit markers
function centerMap() {
    if (mapInstance && coreLib) {
        if (mapMarkers.value.length < 2 && mapMarkers.value[0].position) {
            mapInstance.setCenter(mapMarkers.value[0].position);
            mapInstance.setZoom(15);
        } else {
            const bounds = new coreLib.LatLngBounds();

            mapMarkers.value.forEach((marker: any) => {
                if (marker.position) {
                    bounds.extend(marker.position);
                }
            });

            mapInstance.fitBounds(bounds);
        }
    }
}

onMounted(() => {
    if (!blok.openMapBtn) {
        initGoogleMaps();
    }
});
</script>

<style lang="postcss" scoped>
.product-map {
    @apply relative w-full;

    &:not(:has(button.gm-fullscreen-control)) {
        .map-btn.map-active {
            @apply max-sm:-right-14 max-sm:w-[120px];
            button {
                @apply max-sm:px-1;
            }
            svg {
                @apply max-sm:mr-0;
            }
            span {
                @apply max-sm:inline-block;
            }
        }
    }

    .map-placeholder {
        @apply flex justify-center;
        img {
            @apply h-[140px] w-[350px] md:w-[500px] object-cover rounded-lg;
        }
    }
    .map-btn {
        @apply max-md:w-[200px] absolute left-2/4 top-2/4 -translate-x-2/4 -translate-y-2/4;

        &.map-active {
            @apply max-sm:right-6 max-md:w-auto left-auto right-0 top-8;

            button {
                @apply text-tertiary bg-white border border-tertiary;
                @apply max-sm:text-base max-sm:flex max-sm:items-center max-sm:justify-center;
                &:hover {
                    @apply bg-tertiary text-white;
                }
                svg {
                    @apply max-sm:mr-0;
                }
                span {
                    @apply max-sm:hidden;
                    &.iconify {
                        @apply max-sm:inline-block;
                    }
                }
            }
        }

        button {
            @apply bg-tertiary text-white font-bold text-lg py-2 px-6 rounded flex justify-center items-center gap-x-1;
            &:hover {
                @apply bg-[#f8791e];
            }

            .close-icon {
                @apply h-5 w-5;
            }
        }
    }

    :deep {
        div.gm-style-iw.gm-style-iw-c {
            padding-inline: 0;
            padding-bottom: 0;
            max-height: none !important;
        }
        div.gm-style-iw.gm-style-iw-c div.gm-style-iw-d {
            overflow: unset !important;
            max-height: none !important;
        }
        .product-marker-icon {
            transition: filter ease-in-out 0.2s;
            &:hover {
                filter: brightness(0.9);
            }
        }
    }
}
.map-container {
    @apply w-full h-full min-h-[400px];
}
</style>
